/**
 * Custom App Layout
 *
 * Main layout component that orchestrates sidebar, header, and content.
 * Replaces ProLayout with full control over styling and behavior.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth, useModel } from '@/context/AuthContext';
import { CommandPalette } from '@/components/CommandPalette';
import { CommandPaletteProvider, useCommandPaletteContext } from '@/context/CommandPaletteContext';
import { AIColleaguesProvider, useAIColleaguesModal } from '@/context/AIColleaguesContext';
import { MegaMenuProvider } from '@/context/MegaMenuContext';
import { MegaMenu } from '@/components/MegaMenu';
import { TabsLayout } from '@/components/TabsLayout';
import { AIColleaguesModal } from '@/components/AIColleagues';
import { UpdateBanner } from '@/components/UpdateBanner';
import { usePageContext } from '@/hooks/ai-colleagues';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { applyGlobalLinkColors } from '@/utils/colorUtils';
import { isPermitted, ModuleName, PermissionVerbAction } from '@/components/Guard';
import { useStreamEvent } from '@/services/stream';
import styles from './AppLayout.module.less';

// Storage key for sidebar collapse preference
const SIDEBAR_COLLAPSED_KEY = 'utx-sidebar-collapsed';

/**
 * Inner layout that uses Command Palette and AI Colleagues context
 */
function AppLayoutInner() {
  const { initialState, loading } = useAuth();
  const { setInitialState } = useModel('@@initialState');
  const { isOpen: commandPaletteOpen, close: closeCommandPalette } = useCommandPaletteContext();
  const { open: aiColleaguesOpen, closeAIColleagues, colleagueId, context: modalContext, initialMessage, clearInitialMessage } = useAIColleaguesModal();
  const { context: pageContext, suggestedColleague, tabTitle } = usePageContext();

  // Sidebar collapse state - default to collapsed
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored !== null ? stored === 'true' : true;
  });

  // Hover state for temporary expansion
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle sidebar collapse toggle
  const handleCollapse = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, []);

  // Handle hover to temporarily expand
  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 50);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 150);
  }, []);

  // Cleanup hover timeout
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Note: Primary auth check is handled by AuthGuard component
  // Turnstile verification is handled by TurnstileContext when API returns 406

  // Apply link colors
  useEffect(() => {
    try {
      const settings = initialState?.settings as any;
      const primaryColor = settings?.primaryColor ?? '#1677ff';
      applyGlobalLinkColors(primaryColor);
    } catch {}
  }, [initialState]);

  // Use ref to access initialState without causing re-runs
  const initialStateRef = useRef(initialState);
  initialStateRef.current = initialState;

  // Cross-tab polling logic - run once on mount
  useEffect(() => {
    const POLL_KEY = 'dashboard-last-fetch';
    const DATA_KEY = 'dashboard-latest-data';

    const updateStateFromLocalStorage = () => {
      const dataStr = localStorage.getItem(DATA_KEY);
      if (dataStr) {
        try {
          const data = JSON.parse(dataStr);
          setInitialState((prev: any) => ({ ...prev, ...data }));
        } catch (e) {
          // Ignore parse errors
        }
      }
    };

    const fetchAndBroadcast = async () => {
      const state = initialStateRef.current;
      const now = Date.now();
      const lastFetch = Number(localStorage.getItem(POLL_KEY) || 0);
      const latestData = localStorage.getItem(DATA_KEY);
      if (!latestData || now - lastFetch > 1000 * 60 * 10) {
        localStorage.setItem(POLL_KEY, String(now));
        if (state?.currentUser) {
          const currentUser = state.currentUser;
          const craFormInstructions =
            isPermitted(ModuleName.USER, PermissionVerbAction['VIEW:USER'], currentUser.acl) &&
            isPermitted(ModuleName.USER, PermissionVerbAction['VIEW:USER_PENDING_CRA_FORM'], currentUser.acl)
              ? (await state?.fetchCraFormInstructions())?.total
              : undefined;
          const instructions = isPermitted(
            ModuleName.ALL_INSTRUCTION,
            PermissionVerbAction['VIEW:PENDING_INSTRUCTION'],
            currentUser.acl
          )
            ? await state?.fetchInstructions()
            : undefined;
          const pendingPayments = isPermitted(
            ModuleName.PENDING_PAYMENT,
            PermissionVerbAction['VIEW:PENDING_PAYMENT'],
            currentUser.acl
          )
            ? await state?.fetchPayments()
            : undefined;
          const scheduledInstructions = isPermitted(
            ModuleName.SCHEDULED_INSTRUCTION,
            PermissionVerbAction['VIEW:SCHEDULED_INSTRUCTION'],
            currentUser.acl
          )
            ? await state?.fetchScheduledInstructions()
            : undefined;
          const payments = pendingPayments !== undefined || scheduledInstructions !== undefined
            ? (pendingPayments ?? 0) + (scheduledInstructions ?? 0)
            : undefined;
          const solutionApplications = isPermitted(
            ModuleName.SOLUTION,
            PermissionVerbAction['VIEW:SOLUTION_APPLICATION'],
            currentUser.acl
          )
            ? (await state?.fetchSolutionApplications())?.total
            : undefined;
          const cardApplicationCount = isPermitted(
            ModuleName.CMS,
            PermissionVerbAction['VIEW:CMS_CARD_APPLICATION'],
            currentUser.acl
          )
            ? (await state?.fetchCardApplications())?.total
            : undefined;
          const cardPendingEmbossedCount = isPermitted(
            ModuleName.CMS,
            PermissionVerbAction['VIEW:CMS_PENDING_EMBOSSED'],
            currentUser.acl
          )
            ? (await state?.fetchCardPendingEmbosses())?.total
            : undefined;
          const currencies = isPermitted(ModuleName.USER, PermissionVerbAction['VIEW:USER'], currentUser.acl)
            ? await state?.fetchCurrencies()
            : undefined;
          const countries = isPermitted(ModuleName.USER, PermissionVerbAction['VIEW:USER'], currentUser.acl)
            ? await state?.fetchCountries()
            : undefined;
          const digitalDepositPending = isPermitted(
            ModuleName.DIGITAL,
            PermissionVerbAction['VIEW:DIGITAL_DEPOSIT_PENDING'],
            currentUser.acl
          )
            ? (await state?.fetchDigitalDepositPending())?.total
            : undefined;
          
          // Fetch digital asset stats for rejected deposits and pending withdrawals
          const digitalDepositStats = isPermitted(
            ModuleName.DIGITAL,
            PermissionVerbAction['VIEW:DIGITAL_DEPOSIT'],
            currentUser.acl
          )
            ? await state?.fetchDigitalDepositStats()
            : undefined;
          const digitalWithdrawStats = isPermitted(
            ModuleName.DIGITAL,
            PermissionVerbAction['VIEW:DIGITAL_WITHDRAW'],
            currentUser.acl
          )
            ? await state?.fetchDigitalWithdrawStats()
            : undefined;

          const cashInOutPending = isPermitted(
            ModuleName.CASH_IN_OUT,
            PermissionVerbAction['VIEW:CASH_IN_OUT'],
            currentUser.acl
          )
            ? await state?.fetchCashInOutPending()
            : undefined;
          const earnSubscriptionsPending = isPermitted(
            ModuleName.EARN,
            PermissionVerbAction['VIEW:EARN_SUBSCRIPTION'],
            currentUser.acl
          )
            ? await state?.fetchEarnSubscriptionsPending()
            : undefined;
          const securitiesTransfersPending = isPermitted(
            ModuleName.SECURITIES,
            PermissionVerbAction['VIEW:SECURITIES'],
            currentUser.acl
          )
            ? await state?.fetchSecuritiesTransfersPending()
            : undefined;
          const fundInstructionsPending = isPermitted(
            ModuleName.IFAST_FUND,
            PermissionVerbAction['VIEW:IFAST_INSTRUCTION_LIST'],
            currentUser.acl
          )
            ? await state?.fetchFundInstructionsPending()
            : undefined;
          const preciousMetalPending = isPermitted(
            ModuleName.PRECIOUS_METAL,
            PermissionVerbAction['VIEW:PRECIOUS_METAL_INSTRUCTION'],
            currentUser.acl
          )
            ? await state?.fetchPreciousMetalPending()
            : undefined;

          const newData = {
            craFormInstructions,
            instructions,
            payments,
            scheduledInstructions,
            solutionApplications,
            cardApplicationCount,
            cardPendingEmbossedCount,
            currencies,
            countries,
            digitalDepositPending,
            digitalDepositRejected: digitalDepositStats?.rejected,
            digitalWithdrawPending: (digitalWithdrawStats?.requested ?? 0) + (digitalWithdrawStats?.pending ?? 0),
            cashInOutPending,
            earnSubscriptionsPending,
            securitiesTransfersPending,
            fundInstructionsPending,
            preciousMetalPending,
          };
          localStorage.setItem(DATA_KEY, JSON.stringify(newData));
          setInitialState((prev: any) => ({ ...prev, ...newData }));
        }
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === DATA_KEY && e.newValue) {
        updateStateFromLocalStorage();
      }
    };

    window.addEventListener('storage', onStorage);
    const intervalId = window.setInterval(fetchAndBroadcast, 1000 * 60 * 5);
    updateStateFromLocalStorage();
    fetchAndBroadcast();

    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stream event subscription
  const handleMarketDataEvent = useCallback((message: any, event: any) => {
    console.log(event, message);
  }, []);
  useStreamEvent('marketdata.event.updated', handleMarketDataEvent);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  return (
    <>
      {/* Version update banner - shows when new deployment detected */}
      <UpdateBanner />
      <CommandPalette open={commandPaletteOpen} onClose={closeCommandPalette} />
      <AIColleaguesModal 
        open={aiColleaguesOpen} 
        onClose={closeAIColleagues}
        initialContext={modalContext || (pageContext ? { ...pageContext, tabTitle } : undefined)}
        initialColleagueId={colleagueId || suggestedColleague}
        initialMessage={initialMessage}
        onInitialMessageConsumed={clearInitialMessage}
      />
      <MegaMenu />
      <div className={styles.appLayout}>
        <Sidebar
          collapsed={sidebarCollapsed}
          hovering={isHovering}
          onCollapse={handleCollapse}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        <div className={`${styles.mainLayout} ${!sidebarCollapsed ? styles.mainLayoutSidebarExpanded : ''}`}>
          <Header sidebarCollapsed={sidebarCollapsed} />
          <main className={styles.content}>
            <TabsLayout />
          </main>
        </div>
      </div>
    </>
  );
}

/**
 * App Layout with Command Palette, AI Colleagues, and Mega Menu Providers
 */
export default function AppLayout() {
  return (
    <CommandPaletteProvider>
      <AIColleaguesProvider>
        <MegaMenuProvider>
          <AppLayoutInner />
        </MegaMenuProvider>
      </AIColleaguesProvider>
    </CommandPaletteProvider>
  );
}
